import React, { useState, useEffect } from 'react';
import { apiFetch } from './utils/api';

function SettingsCompanyProfile() {
  const [companyName, setCompanyName] = useState('');
  const [primaryColor, setPrimaryColor] = useState('#12324d');
  const [accentColor, setAccentColor] = useState('#12324d');
  const [logoPreview, setLogoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [saving, setSaving] = useState(false);
  
  // Contact details
  const [addressLine1, setAddressLine1] = useState('');
  const [addressLine2, setAddressLine2] = useState('');
  const [city, setCity] = useState('');
  const [county, setCounty] = useState('');
  const [postcode, setPostcode] = useState('');
  const [phone, setPhone] = useState('');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  
  // Business details
  const [vatNumber, setVatNumber] = useState('');
  const [companyNumber, setCompanyNumber] = useState('');
  const [accountsEmail, setAccountsEmail] = useState('');
  const [defaultSupplyMarkupPercent, setDefaultSupplyMarkupPercent] = useState('40');
  const [defaultServiceMarkupPercent, setDefaultServiceMarkupPercent] = useState('25');
  
  // Bank details
  const [bankName, setBankName] = useState('');
  const [bankAccountName, setBankAccountName] = useState('');
  const [bankSortCode, setBankSortCode] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankIban, setBankIban] = useState('');
  const [bankSwift, setBankSwift] = useState('');
  const [bankRemittanceEmail, setBankRemittanceEmail] = useState('');
  const [bankLegalText, setBankLegalText] = useState('');
  
  // eCap bank details
  const [ecapBankName, setEcapBankName] = useState('');
  const [ecapAccountName, setEcapAccountName] = useState('');
  const [ecapSortCode, setEcapSortCode] = useState('');
  const [ecapAccountNumber, setEcapAccountNumber] = useState('');
  const [ecapIban, setEcapIban] = useState('');
  const [ecapSwift, setEcapSwift] = useState('');
  const [ecapRemittanceEmail, setEcapRemittanceEmail] = useState('');
  const [ecapLegalText, setEcapLegalText] = useState('');
  
  // SMTP settings
  const [smtpHost, setSmtpHost] = useState('');
  const [smtpPort, setSmtpPort] = useState('587');
  const [smtpUser, setSmtpUser] = useState('');
  const [smtpPass, setSmtpPass] = useState('');
  
  // Working hours
  const [workingHoursStart, setWorkingHoursStart] = useState(7);
  const [workingHoursEnd, setWorkingHoursEnd] = useState(19);

  // Load saved settings from API on mount
  useEffect(() => {
    apiFetch('/api/company-settings')
      .then(res => res.json())
      .then(data => {
        if (data.company_name) setCompanyName(data.company_name);
        if (data.primary_color) setPrimaryColor(data.primary_color);
        if (data.accent_color) setAccentColor(data.accent_color);
        if (data.logo_url) setLogoPreview(data.logo_url);
        if (data.address_line1) setAddressLine1(data.address_line1);
        if (data.address_line2) setAddressLine2(data.address_line2);
        if (data.city) setCity(data.city);
        if (data.county) setCounty(data.county);
        if (data.postcode) setPostcode(data.postcode);
        if (data.phone) setPhone(data.phone);
        if (data.mobile) setMobile(data.mobile);
        if (data.email) setEmail(data.email);
        if (data.website) setWebsite(data.website);
        if (data.vat_number) setVatNumber(data.vat_number);
        if (data.company_number) setCompanyNumber(data.company_number);
        if (data.accounts_email) setAccountsEmail(data.accounts_email);
        // Load dual pricing markups (with fallback to old default_markup_percent if needed)
        if (data.default_supply_markup_percent !== undefined) {
          setDefaultSupplyMarkupPercent(data.default_supply_markup_percent);
        } else if (data.default_markup_percent !== undefined) {
          setDefaultSupplyMarkupPercent(data.default_markup_percent); // Migrate old value
        }
        if (data.default_service_markup_percent !== undefined) {
          setDefaultServiceMarkupPercent(data.default_service_markup_percent);
        }
        if (data.bank_name) setBankName(data.bank_name);
        if (data.bank_account_name) setBankAccountName(data.bank_account_name);
        if (data.bank_sort_code) setBankSortCode(data.bank_sort_code);
        if (data.bank_account_number) setBankAccountNumber(data.bank_account_number);
        if (data.bank_iban) setBankIban(data.bank_iban);
        if (data.bank_swift) setBankSwift(data.bank_swift);
        if (data.bank_remittance_email) setBankRemittanceEmail(data.bank_remittance_email);
        if (data.bank_legal_text) setBankLegalText(data.bank_legal_text);
        if (data.ecap_bank_name) setEcapBankName(data.ecap_bank_name);
        if (data.ecap_account_name) setEcapAccountName(data.ecap_account_name);
        if (data.ecap_sort_code) setEcapSortCode(data.ecap_sort_code);
        if (data.ecap_account_number) setEcapAccountNumber(data.ecap_account_number);
        if (data.ecap_iban) setEcapIban(data.ecap_iban);
        if (data.ecap_swift) setEcapSwift(data.ecap_swift);
        if (data.ecap_remittance_email) setEcapRemittanceEmail(data.ecap_remittance_email);
        if (data.ecap_legal_text) setEcapLegalText(data.ecap_legal_text);
        if (data.smtp_host) setSmtpHost(data.smtp_host);
        if (data.smtp_port) setSmtpPort(data.smtp_port);
        if (data.smtp_user) setSmtpUser(data.smtp_user);
        if (data.smtp_pass) setSmtpPass(data.smtp_pass);
        if (data.working_hours_start) setWorkingHoursStart(data.working_hours_start);
        if (data.working_hours_end) setWorkingHoursEnd(data.working_hours_end);
      })
      .catch(err => console.error('Error loading settings:', err));
  }, []);

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await apiFetch('/api/company-settings', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          company_name: companyName,
          logo_url: logoPreview,
          primary_color: primaryColor,
          accent_color: accentColor,
          address_line1: addressLine1,
          address_line2: addressLine2,
          city: city,
          county: county,
          postcode: postcode,
          phone: phone,
          mobile: mobile,
          email: email,
          website: website,
          vat_number: vatNumber,
          company_number: companyNumber,
          bank_name: bankName,
          bank_account_name: bankAccountName,
          bank_sort_code: bankSortCode,
          bank_account_number: bankAccountNumber,
          bank_iban: bankIban,
          bank_swift: bankSwift,
          bank_remittance_email: bankRemittanceEmail,
          bank_legal_text: bankLegalText,
          ecap_bank_name: ecapBankName,
          ecap_account_name: ecapAccountName,
          ecap_sort_code: ecapSortCode,
          ecap_account_number: ecapAccountNumber,
          ecap_iban: ecapIban,
          ecap_swift: ecapSwift,
          ecap_remittance_email: ecapRemittanceEmail,
          ecap_legal_text: ecapLegalText,
          smtp_host: smtpHost,
          smtp_port: smtpPort,
          smtp_user: smtpUser,
          ...(smtpPass ? { smtp_pass: smtpPass } : {}),
          accounts_email: accountsEmail,
          default_supply_markup_percent: parseFloat(defaultSupplyMarkupPercent) || 40,
          default_service_markup_percent: parseFloat(defaultServiceMarkupPercent) || 25,
          working_hours_start: workingHoursStart,
          working_hours_end: workingHoursEnd
        })
      });

      if (response.ok) {
        alert('Company profile saved successfully!');
        // Also save to localStorage as fallback
        localStorage.setItem('companyName', companyName);
        if (logoPreview) {
          localStorage.setItem('companyLogo', logoPreview);
        }
      } else {
        const error = await response.json().catch(() => ({}));
        alert(`Failed to save company profile${error.error ? `: ${error.error}` : ''}`);
      }
    } catch (err) {
      console.error('Error saving settings:', err);
      alert(`Error saving settings${err.message ? `: ${err.message}` : ''}`);
    }
    setSaving(false);
  };

  return (
    <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-lg shadow">
      <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-t-lg">
        <h2 className="text-xl font-semibold">Company Profile</h2>
        <p className="text-sm text-blue-100 mt-1">Configure your company branding and contact details</p>
      </div>
      <div className="p-6 space-y-6">
      {/* Company Logo */}
      <div className="bg-blue-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Company Logo</h3>
        
        <div className="flex items-start space-x-6">
          {/* Logo Preview */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 bg-gray-50 border-2 border-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Company Logo" className="w-full h-full object-contain" />
              ) : (
                <div className="text-center">
                  <div className="text-4xl text-gray-300 mb-2">🏢</div>
                  <p className="text-xs text-gray-400">No logo</p>
                </div>
              )}
            </div>
          </div>

          {/* Upload Controls */}
          <div className="flex-1">
            <label className="block">
              <span className="sr-only">Choose logo file</span>
              <input
                type="file"
                accept="image/png,image/jpeg,image/svg+xml"
                onChange={handleLogoChange}
                className="block w-full text-sm text-gray-500
                  file:mr-4 file:py-2 file:px-4
                  file:rounded-lg file:border-0
                  file:text-sm file:font-semibold
                  file:bg-gray-300 file:text-black
                  hover:file:bg-gray-400
                  cursor-pointer"
              />
            </label>
            <p className="mt-2 text-xs text-gray-500">
              PNG, JPG or SVG. Recommended size: 200x200px. Max file size: 2MB.
            </p>
            {logoFile && (
              <div className="mt-3 flex items-center space-x-2">
                <span className="text-sm text-gray-700">Selected: {logoFile.name}</span>
                <button
                  onClick={() => {
                    setLogoFile(null);
                    setLogoPreview(null);
                  }}
                  className="text-red-600 hover:text-red-700 text-sm font-medium"
                >
                  ✕ Remove
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-xs text-blue-800">
            💡 <strong>Tip:</strong> Your logo will appear in the sidebar and will be visible to all users of your DoorBase account.
          </p>
        </div>
      </div>

      <div className="bg-blue-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Company colours</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Primary colour</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(primaryColor) ? primaryColor : '#12324d'}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="h-10 w-12 rounded border border-gray-300 bg-white"
                aria-label="Primary colour"
              />
              <input
                type="text"
                value={primaryColor}
                onChange={(e) => setPrimaryColor(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm uppercase"
                aria-label="Primary colour hex"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">Header colour on the engineer phone.</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Accent colour</label>
            <div className="flex items-center gap-2">
              <input
                type="color"
                value={/^#[0-9a-fA-F]{6}$/.test(accentColor) ? accentColor : '#12324d'}
                onChange={(e) => setAccentColor(e.target.value)}
                className="h-10 w-12 rounded border border-gray-300 bg-white"
                aria-label="Accent colour"
              />
              <input
                type="text"
                value={accentColor}
                onChange={(e) => setAccentColor(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono text-sm uppercase"
                aria-label="Accent colour hex"
              />
            </div>
            <p className="mt-2 text-xs text-gray-500">Contact labels and key buttons on the engineer phone.</p>
          </div>
        </div>
      </div>

      {/* Company Name */}
      <div className="bg-blue-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Company Name</h3>
        
        <div className="max-w-md">
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Business Name
          </label>
          <input
            type="text"
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
            placeholder="e.g., Automated Gate Services"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
          />
          <p className="mt-2 text-xs text-gray-500">
            This name appears in the sidebar and throughout the application.
          </p>
        </div>
      </div>

      {/* Address & Contact Details */}
      <div className="bg-blue-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Address & Contact Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 1
            </label>
            <input
              type="text"
              value={addressLine1}
              onChange={(e) => setAddressLine1(e.target.value)}
              placeholder="Street address"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Address Line 2
            </label>
            <input
              type="text"
              value={addressLine2}
              onChange={(e) => setAddressLine2(e.target.value)}
              placeholder="Building, suite, etc. (optional)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              City/Town
            </label>
            <input
              type="text"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              placeholder="e.g., Colchester"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              County
            </label>
            <input
              type="text"
              value={county}
              onChange={(e) => setCounty(e.target.value)}
              placeholder="e.g., Essex"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Postcode
            </label>
            <input
              type="text"
              value={postcode}
              onChange={(e) => setPostcode(e.target.value)}
              placeholder="e.g., CO4 9QX"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Phone Number
            </label>
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="e.g., 01206 123456"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Mobile Number
            </label>
            <input
              type="tel"
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              placeholder="e.g., 07700 900123"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g., info@yourcompany.co.uk"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Website
            </label>
            <input
              type="url"
              value={website}
              onChange={(e) => setWebsite(e.target.value)}
              placeholder="e.g., www.yourcompany.co.uk"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
      </div>

      {/* Business Registration Details */}
      <div className="bg-blue-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Business Registration Details</h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VAT Number
            </label>
            <input
              type="text"
              value={vatNumber}
              onChange={(e) => setVatNumber(e.target.value)}
              placeholder="e.g., GB123456789"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Company Registration Number
            </label>
            <input
              type="text"
              value={companyNumber}
              onChange={(e) => setCompanyNumber(e.target.value)}
              placeholder="e.g., 12345678"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Accounts Email
            </label>
            <input
              type="email"
              value={accountsEmail}
              onChange={(e) => setAccountsEmail(e.target.value)}
              placeholder="e.g., accounts@yourcompany.co.uk"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
            <p className="mt-2 text-xs text-gray-500">
              This email will be used for invoices and financial communications.
            </p>
          </div>

          {/* Dual Pricing Markups */}
          <div className="col-span-2">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Dual Pricing Defaults</h4>
            <p className="text-xs text-gray-600 mb-3">
              Two-tier pricing: Supply Price (Sales Orders) → Service Price (Jobs)
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <label className="block text-sm font-medium text-blue-900 mb-2">
                  Supply Markup % (Tier 1)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={defaultSupplyMarkupPercent}
                  onChange={(e) => setDefaultSupplyMarkupPercent(e.target.value)}
                  placeholder="40"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="mt-1 text-xs text-blue-700">
                  For Sales Orders (supply-only customers)
                </p>
              </div>
              
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <label className="block text-sm font-medium text-green-900 mb-2">
                  Service Markup % (Tier 2)
                </label>
                <input
                  type="number"
                  step="0.1"
                  value={defaultServiceMarkupPercent}
                  onChange={(e) => setDefaultServiceMarkupPercent(e.target.value)}
                  placeholder="25"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                />
                <p className="mt-1 text-xs text-green-700">
                  For Jobs (service work with materials)
                </p>
              </div>
            </div>
            <p className="mt-2 text-xs text-gray-500">
              💡 Example: £100 cost → £{(100 * (1 + (parseFloat(defaultSupplyMarkupPercent) || 40) / 100)).toFixed(2)} supply → £{(100 * (1 + (parseFloat(defaultSupplyMarkupPercent) || 40) / 100) * (1 + (parseFloat(defaultServiceMarkupPercent) || 25) / 100)).toFixed(2)} service
            </p>
          </div>
        </div>
      </div>

      {/* Working Hours */}
      <div className="bg-blue-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">⏰ Working Hours</h3>
        <p className="text-sm text-gray-600 mb-4">
          Set default working hours for dispatch board and scheduling. These highlight working time on the timeline view.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Start Time (24-hour format)
            </label>
            <select
              value={workingHoursStart}
              onChange={(e) => setWorkingHoursStart(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              End Time (finish hour)
            </label>
            <select
              value={workingHoursEnd}
              onChange={(e) => setWorkingHoursEnd(parseInt(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {Array.from({ length: 24 }, (_, i) => (
                <option key={i} value={i}>
                  {i === 0 ? '12am' : i < 12 ? `${i}am` : i === 12 ? '12pm' : `${i - 12}pm`}
                </option>
              ))}
            </select>
            <p className="mt-1 text-xs text-gray-500">
              e.g., 5pm = work ends at 5pm (last hour slot is 4-5pm)
            </p>
          </div>
        </div>
        
        <p className="mt-3 text-xs text-gray-500">
          Current: {workingHoursStart === 0 ? '12am' : workingHoursStart < 12 ? `${workingHoursStart}am` : workingHoursStart === 12 ? '12pm' : `${workingHoursStart - 12}pm`}
          {' '}-{' '}
          {workingHoursEnd === 0 ? '12am' : workingHoursEnd < 12 ? `${workingHoursEnd}am` : workingHoursEnd === 12 ? '12pm' : `${workingHoursEnd - 12}pm`}
          {' '}({workingHoursEnd - workingHoursStart} hours)
        </p>
      </div>

      {/* Bank Details Section */}
      <div className="bg-green-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">🏦 Bank Details</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure your company's bank details for invoices. Choose between your own account or eCap factoring.
        </p>
        
        {/* Our Bank Details */}
        <div className="mb-6">
          <h4 className="text-md font-semibold text-gray-800 mb-3">Your Bank Account</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
              <input
                type="text"
                value={bankName}
                onChange={(e) => setBankName(e.target.value)}
                placeholder="e.g., Barclays Bank"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
              <input
                type="text"
                value={bankAccountName}
                onChange={(e) => setBankAccountName(e.target.value)}
                placeholder="e.g., Your Company Ltd"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort Code</label>
              <input
                type="text"
                value={bankSortCode}
                onChange={(e) => setBankSortCode(e.target.value)}
                placeholder="00-00-00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
              <input
                type="text"
                value={bankAccountNumber}
                onChange={(e) => setBankAccountNumber(e.target.value)}
                placeholder="00000000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">IBAN <span className="text-gray-400">(Optional - for international)</span></label>
              <input
                type="text"
                value={bankIban}
                onChange={(e) => setBankIban(e.target.value)}
                placeholder="GB00 XXXX 0000 0000 0000 00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SWIFT/BIC <span className="text-gray-400">(Optional)</span></label>
              <input
                type="text"
                value={bankSwift}
                onChange={(e) => setBankSwift(e.target.value)}
                placeholder="BARCGB22"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remittance Email <span className="text-gray-400">(Where customers send payment confirmations)</span></label>
              <input
                type="email"
                value={bankRemittanceEmail}
                onChange={(e) => setBankRemittanceEmail(e.target.value)}
                placeholder="accounts@yourcompany.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Legal Wording <span className="text-gray-400">(Appears on invoices using Your Bank)</span></label>
              <textarea
                value={bankLegalText}
                onChange={(e) => setBankLegalText(e.target.value)}
                rows="3"
                placeholder="e.g., Payment due within 30 days. Late payments subject to interest charges."
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
        </div>

        {/* eCap Bank Details */}
        <div className="border-t border-gray-300 pt-6">
          <h4 className="text-md font-semibold text-gray-800 mb-3">eCap Bank Account (Invoice Factoring)</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bank Name</label>
              <input
                type="text"
                value={ecapBankName}
                onChange={(e) => setEcapBankName(e.target.value)}
                placeholder="e.g., eCap"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Name</label>
              <input
                type="text"
                value={ecapAccountName}
                onChange={(e) => setEcapAccountName(e.target.value)}
                placeholder="e.g., eCap re Your Company Ltd"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Sort Code</label>
              <input
                type="text"
                value={ecapSortCode}
                onChange={(e) => setEcapSortCode(e.target.value)}
                placeholder="00-00-00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Account Number</label>
              <input
                type="text"
                value={ecapAccountNumber}
                onChange={(e) => setEcapAccountNumber(e.target.value)}
                placeholder="00000000"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">IBAN <span className="text-gray-400">(Optional)</span></label>
              <input
                type="text"
                value={ecapIban}
                onChange={(e) => setEcapIban(e.target.value)}
                placeholder="GB00 XXXX 0000 0000 0000 00"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">SWIFT/BIC <span className="text-gray-400">(Optional)</span></label>
              <input
                type="text"
                value={ecapSwift}
                onChange={(e) => setEcapSwift(e.target.value)}
                placeholder="XXXXGB22"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">Remittance Email <span className="text-gray-400">(Where eCap sends payment confirmations)</span></label>
              <input
                type="email"
                value={ecapRemittanceEmail}
                onChange={(e) => setEcapRemittanceEmail(e.target.value)}
                placeholder="remittance@ecap.com"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </div>
          
          <div className="mt-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              eCap Legal Wording <span className="text-gray-400">(Shown on invoices when eCap bank details are selected)</span>
            </label>
            <textarea
              value={ecapLegalText}
              onChange={(e) => setEcapLegalText(e.target.value)}
              rows="3"
              placeholder="e.g., This invoice has been assigned to eCap Financial Services Ltd. Payment should be made to the account details shown above."
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            />
          </div>
        </div>
      </div>

      {/* SMTP Settings Section */}
      <div className="bg-blue-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900">📧 Email Settings (SMTP)</h3>
        <p className="text-sm text-gray-600 mb-4">
          Configure your email server to send quotes, invoices, and other documents.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Host <span className="text-gray-400">(e.g., smtp.gmail.com)</span>
            </label>
            <input
              type="text"
              value={smtpHost}
              onChange={(e) => setSmtpHost(e.target.value)}
              placeholder="smtp.gmail.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Port <span className="text-gray-400">(Usually 587)</span>
            </label>
            <input
              type="number"
              value={smtpPort}
              onChange={(e) => setSmtpPort(e.target.value)}
              placeholder="587"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Username <span className="text-gray-400">(Email address)</span>
            </label>
            <input
              type="email"
              value={smtpUser}
              onChange={(e) => setSmtpUser(e.target.value)}
              placeholder="your-email@gmail.com"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              SMTP Password <span className="text-gray-400">(App Password for Gmail)</span>
            </label>
            <input
              type="password"
              value={smtpPass}
              onChange={(e) => setSmtpPass(e.target.value)}
              placeholder="••••••••••••••••"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          <div className="md:col-span-2">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Gmail users:</strong> Use an App Password instead of your regular password. 
                <a href="https://support.google.com/accounts/answer/185833" target="_blank" rel="noopener noreferrer" className="underline ml-1">
                  Learn how to create one →
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Preview Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-300 p-6 mb-6">
        <h3 className="text-sm font-semibold text-gray-700 mb-4">Preview</h3>
        <p className="text-xs text-gray-600 mb-4">How your branding will appear in the sidebar:</p>
        
        <div className="w-32 bg-white border border-gray-200 rounded-lg p-3">
          <div className="text-center">
            <div className="w-16 h-16 mx-auto bg-white border-2 border-gray-200 rounded-lg flex items-center justify-center mb-2 overflow-hidden">
              {logoPreview ? (
                <img src={logoPreview} alt="Preview" className="w-full h-full object-contain" />
              ) : (
                <span className="text-gray-400 text-xs">Logo</span>
              )}
            </div>
            <p className="text-xs font-medium text-gray-900 truncate">{companyName}</p>
          </div>
        </div>
      </div>

      {/* Save Button */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200 bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-b-lg">
        <p className="text-sm text-blue-100">
          Changes will be applied immediately for all users.
        </p>
        <button
          onClick={handleSave}
          className="px-6 py-3 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-semibold shadow-sm transition"
        >
          💾 Save Changes
        </button>
      </div>
      </div>
    </div>
  );
}

export default SettingsCompanyProfile;
